import { TextSkeleton } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack } from "@mui/material";
import { CancellationCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CancellationCreditProgressBar";
import { formatDurationRangeLabel } from "@src/appV2/redesign/utils/formatDurationLabel";

import { CancelShiftFooter } from "./components/CancelShiftFooter";
import { CancelShiftHeader } from "./components/CancelShiftHeader";
import { ScoreBreakdownList } from "./components/ScoreBreakdownList";
import { ScoreDisplay } from "./components/ScoreDisplay";
import type { CancelShiftPageData } from "./hooks/useCancelShiftPageData";

export interface CancelShiftPageContentHandlers {
  onKeepShift: () => void;
  onPressAndHoldToCancel: () => void;
  onPreviewCredit: () => void;
  onWhyCantIUseCredit?: () => void;
}

export type CancelShiftPageContentProps = CancelShiftPageData & CancelShiftPageContentHandlers;

function CancelShiftPageContentSkeleton(): JSX.Element {
  return (
    <>
      <CancelShiftHeader />
      <Stack spacing={4} alignItems="center" sx={{ paddingY: 8, paddingX: 6 }}>
        <TextSkeleton variant="h2" width="60%" />
        <TextSkeleton variant="body2" width="80%" />
        <TextSkeleton variant="h1" width="30%" />
        <TextSkeleton variant="body2" width="100%" />
        <TextSkeleton variant="body2" width="100%" />
      </Stack>
    </>
  );
}

export function CancelShiftPageContent(props: CancelShiftPageContentProps): JSX.Element | null {
  if (props.isLoading) {
    return <CancelShiftPageContentSkeleton />;
  }

  if (props.isError) {
    return (
      <>
        <CancelShiftHeader />
        <Alert severity="error" sx={{ margin: 4 }}>
          Something went wrong while loading cancellation details. Please try again.
        </Alert>
      </>
    );
  }

  const {
    cancellationPolicy,
    currentScore,
    scoreChange,
    emergencyCancellationCreditStats,
    hasCancellationCredit,
    canUseCredit,
    onKeepShift,
    onPressAndHoldToCancel,
    onPreviewCredit,
    onWhyCantIUseCredit,
  } = props;

  const [from, to] = cancellationPolicy?.leadTime ?? [0, undefined];
  const policyLabel = `${formatDurationRangeLabel(from ?? 0, to ?? undefined, "hour", {
    format: "abbreviated",
    keepOriginalUnit: true,
  })} cancellation`;

  const newScore = currentScore + scoreChange;
  const showWhyCantIUseCredit =
    !canUseCredit && hasCancellationCredit && typeof onWhyCantIUseCredit === "function";

  return (
    <>
      <CancelShiftHeader />

      {isDefined(emergencyCancellationCreditStats) && (
        <Stack sx={{ flex: 1 }}>
          <ScoreDisplay score={newScore} />

          <Stack sx={{ paddingX: 6, paddingY: 4 }}>
            <CancellationCreditProgressBar
              showShiftsProgress={!canUseCredit}
              cancellationCreditStats={emergencyCancellationCreditStats}
            />
          </Stack>

          <ScoreBreakdownList
            currentScore={currentScore}
            scoreChange={scoreChange}
            policyLabel={policyLabel}
          />
        </Stack>
      )}

      <CancelShiftFooter
        variant={canUseCredit ? "preview-credit" : "cancel-shift"}
        showWhyCantIUseCredit={showWhyCantIUseCredit}
        onKeepShift={onKeepShift}
        onPressAndHoldToCancel={onPressAndHoldToCancel}
        onPreviewCredit={onPreviewCredit}
        onWhyCantIUseCredit={onWhyCantIUseCredit}
      />
    </>
  );
}
